import { useState } from "react";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { useFieldArray } from "react-hook-form";

import { Pill } from "src/base-components/Pill";
import { Button } from "src/design-system/Button";
import { SchemaEnumItem } from "src/schema/enumEditor/SchemaEnumItem";
import { SchemaEnumItemInput } from "src/schema/enumEditor/SchemaEnumItemInput";
import { SchemaUI } from "src/schema/schemaMappingUtils";

const MAX_ENUM_VALUES = 11;

const getNextEnumValue = (fieldsLength: number) => {
  return `"value_${fieldsLength + 1}"`;
};

export const SchemaEnumsEditor: React.FC<{
  immutable: boolean;
  propertyIndex: number;
  onShowAll: () => void;
}> = ({ immutable, propertyIndex, onShowAll }) => {
  const [newEnumValue, setNewEnumValue] = useState<string | null>(null);
  const { fields, prepend, remove } = useFieldArray<SchemaUI>({
    name: `properties.${propertyIndex}.enum`,
  });

  const fieldsToRender = fields.slice(0, MAX_ENUM_VALUES);
  const extraFieldsLength = fields.length - MAX_ENUM_VALUES;

  return (
    <ol className="my-4 ml-7 flex flex-row flex-wrap gap-2">
      {fieldsToRender.map((field, index) => (
        <SchemaEnumItem
          key={field.id}
          deletable={fields.length !== 1}
          enumIndex={index}
          propertyIndex={propertyIndex}
          readonly={immutable}
          onDelete={() => remove(index)}
        />
      ))}
      {extraFieldsLength > 0 && (
        <li className="flex self-center">
          <Pill size="sm" variant="gray" onClick={onShowAll}>
            <Pill.Text>+{extraFieldsLength} more</Pill.Text>
          </Pill>
        </li>
      )}
      {newEnumValue && (
        <li className="flex self-center">
          <SchemaEnumItemInput
            dataLoc={`schema-add-enum-input-${propertyIndex}`}
            value={newEnumValue}
            onCancel={() => setNewEnumValue(null)}
            onCommit={(value) => {
              prepend({ value });
              setNewEnumValue(null);
            }}
          />
        </li>
      )}
      <li className="flex self-center">
        <Button
          dataLoc="add-enum-value"
          isDisabled={immutable}
          iconLeft={faPlus}
          size="sm"
          variant="secondary"
          onClick={() => {
            if (newEnumValue) {
              prepend({ value: newEnumValue });
              setNewEnumValue(getNextEnumValue(fields.length + 2));
            } else {
              setNewEnumValue(getNextEnumValue(fields.length + 1));
            }
          }}
        >
          Add value
        </Button>
      </li>
    </ol>
  );
};
